// King attack. The line's first move goes for the king: it gives check, or
// takes something on a square beside the king. The target names the
// variation: `f2f7` when the move lands on the enemy's f2/f7 with a capture
// or a check; `exposedKing` when the king has fewer than two of its own
// pawns on the six squares in front of it; otherwise `kingside` or
// `queenside` by where the king stands (a king in the centre behind its
// shield is `exposedKing` as well). Except on f2/f7, the line must win
// material or mate: a bare check that wins nothing is not an attack.
import type { Motif } from '@chess/api-mistakes';
import type { TacticLine } from './line.ts';
import { distance, fileOf, forward, kingSquare, moved, rankOf, squareAt } from './rays.ts';

/** A king with fewer than this many pawns in front of it stands exposed. */
export const SHIELD_PAWNS = 2;

export function kingAttack(line: TacticLine): Motif | undefined {
  const first = line.pv[0];
  const m = first ? moved(line.board, first) : undefined;
  const king = m ? kingSquare(m.after, line.victim) : undefined;
  if (!m || !king) return undefined;
  const checks = m.after.inCheck(line.victim);
  const target = line.victim === 'white' ? 'f2' : 'f7';
  if (m.to === target && (checks || m.captured)) return { family: 'kingAttack', variation: 'f2f7' };
  if (!checks && !(m.captured && distance(m.to, king) === 1)) return undefined;
  if (!line.material && !((line.mate ?? 0) > 0)) return undefined;
  const kf = fileOf(king);
  const kr = rankOf(king);
  let shield = 0;
  for (const dr of [1, 2]) {
    for (const df of [-1, 0, 1]) {
      const sq = squareAt(kf + df, kr + dr * forward(line.victim));
      const p = sq ? m.after.get(sq) : undefined;
      if (p && p.kind === 'pawn' && p.colour === line.victim) shield++;
    }
  }
  if (shield < SHIELD_PAWNS) return { family: 'kingAttack', variation: 'exposedKing' };
  return { family: 'kingAttack', variation: kf >= 5 ? 'kingside' : kf <= 2 ? 'queenside' : 'exposedKing' };
}
